import {useCallback} from "react";
import {useDispatch} from "react-redux";

import {omit, range} from "@pg-mono/nodash";
import {UserComAttribute, UserComCustomAttribute, userComUpdateUserAttributes, userComUserengage} from "@pg-mono/user-com";
import {sessionCache} from "@pg-rp/session-cache";

import {rpApi} from "../../app/api/rp_api";
import {NEWSLETTER_SEARCH_DATA_STORAGE_KEY} from "../../app/constants/storage_keys";
import {addFailureNotification} from "../../notifications/utils/add_failure_notification";
import {addSuccessNotification} from "../../notifications/utils/add_success_notification";
import {IOfferNotificationChatBotFormValues, PhoneType} from "../../offer/constants/offer_notification_chatbot_form";
import {OfferNotificationAttribute} from "../../offer/constants/offer_notification_user_com";
import {getPriceMessage} from "../../offer/list/utils/get_price_message";
import {useGetUserProfileQuery} from "../../user/api/get_user_profile";
import {patchUserConsent} from "../../user/api/patch_user_consent";
import {IUserProfile} from "../../user/types/IUserProfile";
import {MarketingConsentType} from "../../user/types/MarketingConsentType";
import {SubscribeToNotificationsPayload, useSubscribeToNotificationsMutation} from "../api/subscribe_to_notifications";
import {NewsletterSubscriptionPayloadType} from "../types/NewsletterSubscriptionPayloadType";

const notificationSubscribeEventName = "event.notification_subscribe";

export const useSubscribeToNewsletter = () => {
    const dispatch = useDispatch();
    const [subscribe] = useSubscribeToNotificationsMutation();
    const {data: initialUserData} = useGetUserProfileQuery();

    const getNewsletterSearchData = (): NewsletterSubscriptionPayloadType | undefined => {
        return sessionCache.get<NewsletterSubscriptionPayloadType>(NEWSLETTER_SEARCH_DATA_STORAGE_KEY) ?? {};
    };

    const saveNewsletterSearchData = (data: NewsletterSubscriptionPayloadType) => {
        sessionCache.set(NEWSLETTER_SEARCH_DATA_STORAGE_KEY, data);
    };

    const subscribeToNotifications = useCallback(
        async (userData: Partial<IUserProfile>, ignoreFilters = false, showNotification = true) => {
            const {newsletterPayload, userComPayload, userComBasePayload, userComSearchPayload} = transformDataForNewsletterPayloads(
                userData,
                {emailConsent: initialUserData?.email_marketing_consent ?? false, phoneConsent: initialUserData?.phone_marketing_consent ?? false},
                !ignoreFilters ? getNewsletterSearchData() : {}
            );

            try {
                await subscribe(newsletterPayload).unwrap();
                if (
                    initialUserData &&
                    initialUserData.email === newsletterPayload.email &&
                    (initialUserData.email_marketing_consent !== userData.email_marketing_consent ||
                        initialUserData.phone_marketing_consent !== userData.phone_marketing_consent)
                ) {
                    await patchUserConsent({
                        [MarketingConsentType.PHONE]: newsletterPayload.phone_marketing_consent,
                        [MarketingConsentType.EMAIL]: newsletterPayload.email_marketing_consent
                    });
                    dispatch(rpApi.util.invalidateTags(["UserProfile"]));
                }

                userComUpdateUserAttributes(userComBasePayload);
                userComUserengage(notificationSubscribeEventName, {...userComPayload, ...userComSearchPayload});

                showNotification && dispatch(addSuccessNotification("Pomyślnie zapisano na powiadomienia."));
            } catch {
                showNotification && dispatch(addFailureNotification("Nie udało się zapisać na powiadomienia. Spróbuj ponownie później."));
            }
        },
        [subscribe, initialUserData]
    );

    return {subscribeToNotifications, saveNewsletterSearchData, getNewsletterSearchData};
};

const transformDataForNewsletterPayloads = (
    userData: Partial<IUserProfile>,
    userOriginalConsents: {emailConsent: boolean; phoneConsent: boolean},
    searchData?: NewsletterSubscriptionPayloadType
) => {
    const preparedSearchData = searchData && omit(searchData, ["phone_type"]);

    const phone_marketing_consent = userData.phone_marketing_consent || userOriginalConsents.phoneConsent;
    const email_marketing_consent = userData.email_marketing_consent || userOriginalConsents.emailConsent;

    const newsletterPayload: SubscribeToNotificationsPayload = {
        email: userData.email || "",
        ...(userData.phone ? {phone: userData.phone} : {}),
        general_marketing_consent: (email_marketing_consent && phone_marketing_consent) || false,
        phone_marketing_consent,
        email_marketing_consent,
        search_data: preparedSearchData ?? {}
    };

    const userComBasePayload = {
        event_name: notificationSubscribeEventName,
        [UserComAttribute.EMAIL]: userData.email || "",
        ...(userData.phone ? {[UserComAttribute.PHONE_NUMBER]: userData.phone} : {})
    };

    const userComPayload = {
        ...userComBasePayload,
        [UserComCustomAttribute.EMAIL_MARKETING_CONSENT]: email_marketing_consent ? "tak" : "nie",
        [UserComCustomAttribute.PHONE_MARKETING_CONSENT]: phone_marketing_consent ? "tak" : "nie"
    };

    let userComSearchPayload = {};

    if (searchData) {
        const regionStats = searchData.search?.regions?.[0]?.stats;
        const voivodeship = regionStats?.region_type_voivodeship?.name;
        const city = regionStats?.region_type_city?.name;
        const district = regionStats?.region_type_district?.name;
        const priceRange = searchData.price || null;
        userComSearchPayload = {
            ...(voivodeship ? {[OfferNotificationAttribute.VOIVODESHIP]: voivodeship} : {}),
            ...(city ? {[OfferNotificationAttribute.CITY]: city} : {}),
            ...(district ? {[OfferNotificationAttribute.DISTRICT]: district} : {}),
            [OfferNotificationAttribute.ROOMS]: getRoomList(searchData.rooms).join(","),
            ...(searchData.phone_type === PhoneType.Sms ? {[OfferNotificationAttribute.PHONE_MARKETING_CONSENT]: "tak"} : {}),
            ...(searchData.phone_type === PhoneType.Whatsapp ? {[OfferNotificationAttribute.WHATSAPP_MARKETING_CONSENT]: "tak"} : {}),
            ...(priceRange ? {[OfferNotificationAttribute.PRICE]: getPriceMessage(priceRange.lower, priceRange.upper)} : {}),
            ...(priceRange?.lower ? {[OfferNotificationAttribute.PRICE_LOWER_LIMIT]: priceRange.lower} : {}),
            ...(priceRange?.upper ? {[OfferNotificationAttribute.PRICE_UPPER_LIMIT]: priceRange.upper} : {})
        };
    }

    return {newsletterPayload, userComBasePayload, userComPayload, userComSearchPayload};
};

const getRoomList = (rooms?: IOfferNotificationChatBotFormValues["rooms"]) => {
    if (!rooms || (!rooms.lower && !rooms.upper)) {
        return [];
    }

    const roomLower = rooms.lower || 1;
    const roomUpper = rooms.upper || 5;

    return range(roomLower, roomUpper + 1);
};
import {useCallback} from "react";
import {useDispatch} from "react-redux";

import {omit, range} from "@pg-mono/nodash";
import {UserComAttribute, UserComCustomAttribute, userComUpdateUserAttributes, userComUserengage} from "@pg-mono/user-com";
import {sessionCache} from "@pg-rp/session-cache";

import {rpApi} from "../../app/api/rp_api";
import {NEWSLETTER_SEARCH_DATA_STORAGE_KEY} from "../../app/constants/storage_keys";
import {addFailureNotification} from "../../notifications/utils/add_failure_notification";
import {addSuccessNotification} from "../../notifications/utils/add_success_notification";
import {IOfferNotificationChatBotFormValues, PhoneType} from "../../offer/constants/offer_notification_chatbot_form";
import {OfferNotificationAttribute} from "../../offer/constants/offer_notification_user_com";
import {getPriceMessage} from "../../offer/list/utils/get_price_message";
import {useGetUserProfileQuery} from "../../user/api/get_user_profile";
import {patchUserConsent} from "../../user/api/patch_user_consent";
import {IUserProfile} from "../../user/types/IUserProfile";
import {MarketingConsentType} from "../../user/types/MarketingConsentType";
import {SubscribeToNotificationsPayload, useSubscribeToNotificationsMutation} from "../api/subscribe_to_notifications";
import {NewsletterSubscriptionPayloadType} from "../types/NewsletterSubscriptionPayloadType";

const notificationSubscribeEventName = "event.notification_subscribe";

export const useSubscribeToNewsletter = () => {
    const dispatch = useDispatch();
    const [subscribe] = useSubscribeToNotificationsMutation();
    const {data: initialUserData} = useGetUserProfileQuery();

    const getNewsletterSearchData = (): NewsletterSubscriptionPayloadType | undefined => {
        return sessionCache.get<NewsletterSubscriptionPayloadType>(NEWSLETTER_SEARCH_DATA_STORAGE_KEY) ?? {};
    };

    const saveNewsletterSearchData = (data: NewsletterSubscriptionPayloadType) => {
        sessionCache.set(NEWSLETTER_SEARCH_DATA_STORAGE_KEY, data);
    };

    const subscribeToNotifications = useCallback(
        async (userData: Partial<IUserProfile>, ignoreFilters = false, showNotification = true) => {
            const {newsletterPayload, userComPayload, userComBasePayload, userComSearchPayload} = transformDataForNewsletterPayloads(
                userData,
                {emailConsent: initialUserData?.email_marketing_consent ?? false, phoneConsent: initialUserData?.phone_marketing_consent ?? false},
                !ignoreFilters ? getNewsletterSearchData() : {}
            );

            try {
                await subscribe(newsletterPayload).unwrap();
                if (
                    initialUserData &&
                    initialUserData.email === newsletterPayload.email &&
                    (initialUserData.email_marketing_consent !== userData.email_marketing_consent ||
                        initialUserData.phone_marketing_consent !== userData.phone_marketing_consent)
                ) {
                    await patchUserConsent({
                        [MarketingConsentType.PHONE]: newsletterPayload.phone_marketing_consent,
                        [MarketingConsentType.EMAIL]: newsletterPayload.email_marketing_consent
                    });
                    dispatch(rpApi.util.invalidateTags(["UserProfile"]));
                }

                userComUpdateUserAttributes(userComBasePayload);
                userComUserengage(notificationSubscribeEventName, {...userComPayload, ...userComSearchPayload});

                showNotification && dispatch(addSuccessNotification("Pomyślnie zapisano na powiadomienia."));
            } catch {
                showNotification && dispatch(addFailureNotification("Nie udało się zapisać na powiadomienia. Spróbuj ponownie później."));
            }
        },
        [subscribe, initialUserData]
    );

    return {subscribeToNotifications, saveNewsletterSearchData, getNewsletterSearchData};
};

const transformDataForNewsletterPayloads = (
    userData: Partial<IUserProfile>,
    userOriginalConsents: {emailConsent: boolean; phoneConsent: boolean},
    searchData?: NewsletterSubscriptionPayloadType
) => {
    const preparedSearchData = searchData && omit(searchData, ["phone_type"]);

    const phone_marketing_consent = userData.phone_marketing_consent || userOriginalConsents.phoneConsent;
    const email_marketing_consent = userData.email_marketing_consent || userOriginalConsents.emailConsent;

    const newsletterPayload: SubscribeToNotificationsPayload = {
        email: userData.email || "",
        ...(userData.phone ? {phone: userData.phone} : {}),
        general_marketing_consent: (email_marketing_consent && phone_marketing_consent) || false,
        phone_marketing_consent,
        email_marketing_consent,
        search_data: preparedSearchData ?? {}
    };

    const userComBasePayload = {
        event_name: notificationSubscribeEventName,
        [UserComAttribute.EMAIL]: userData.email || "",
        ...(userData.phone ? {[UserComAttribute.PHONE_NUMBER]: userData.phone} : {})
    };

    const userComPayload = {
        ...userComBasePayload,
        [UserComCustomAttribute.EMAIL_MARKETING_CONSENT]: email_marketing_consent ? "tak" : "nie",
        [UserComCustomAttribute.PHONE_MARKETING_CONSENT]: phone_marketing_consent ? "tak" : "nie"
    };

    let userComSearchPayload = {};

    if (searchData) {
        const regionStats = searchData.search?.regions?.[0]?.stats;
        const voivodeship = regionStats?.region_type_voivodeship?.name;
        const city = regionStats?.region_type_city?.name;
        const district = regionStats?.region_type_district?.name;
        const priceRange = searchData.price || null;
        userComSearchPayload = {
            ...(voivodeship ? {[OfferNotificationAttribute.VOIVODESHIP]: voivodeship} : {}),
            ...(city ? {[OfferNotificationAttribute.CITY]: city} : {}),
            ...(district ? {[OfferNotificationAttribute.DISTRICT]: district} : {}),
            [OfferNotificationAttribute.ROOMS]: getRoomList(searchData.rooms).join(","),
            ...(searchData.phone_type === PhoneType.Sms ? {[OfferNotificationAttribute.PHONE_MARKETING_CONSENT]: "tak"} : {}),
            ...(searchData.phone_type === PhoneType.Whatsapp ? {[OfferNotificationAttribute.WHATSAPP_MARKETING_CONSENT]: "tak"} : {}),
            ...(priceRange ? {[OfferNotificationAttribute.PRICE]: getPriceMessage(priceRange.lower, priceRange.upper)} : {}),
            ...(priceRange?.lower ? {[OfferNotificationAttribute.PRICE_LOWER_LIMIT]: priceRange.lower} : {}),
            ...(priceRange?.upper ? {[OfferNotificationAttribute.PRICE_UPPER_LIMIT]: priceRange.upper} : {})
        };
    }

    return {newsletterPayload, userComBasePayload, userComPayload, userComSearchPayload};
};

const getRoomList = (rooms?: IOfferNotificationChatBotFormValues["rooms"]) => {
    if (!rooms || (!rooms.lower && !rooms.upper)) {
        return [];
    }

    const roomLower = rooms.lower || 1;
    const roomUpper = rooms.upper || 5;

    return range(roomLower, roomUpper + 1);
};
